import { useEffect, type FormEvent, type ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface WizardModalShellProps {
    title: ReactNode;
    onClose: () => void;
    /** Escape and overlay-click are both disabled while a save is in flight. */
    dismissible: boolean;
    /** id given to the heading, for aria-labelledby. */
    labelledBy: string;
    stepIndicator: ReactNode;
    onSubmit: (e: FormEvent) => void;
    /** Right-aligned Continue/Submit button(s); Cancel is built in. */
    primaryActions: ReactNode;
    summaryPane?: ReactNode;
    children: ReactNode;
}

// Fixed height so the stepper swaps content between steps without the modal
// resizing. Dialog contract mirrors ModalShell.tsx.
export function WizardModalShell({
    title,
    onClose,
    dismissible,
    labelledBy,
    stepIndicator,
    onSubmit,
    primaryActions,
    summaryPane,
    children
}: WizardModalShellProps) {
    useEffect(() => {
        if (!dismissible) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [dismissible, onClose]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-6"
            onClick={dismissible ? onClose : undefined}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-[940px] h-[min(680px,90vh)] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 pt-4 pb-0 border-b border-gray-200 shrink-0">
                    <div className="flex items-center justify-between">
                        <h2 id={labelledBy} className="text-lg font-bold text-gray-800">
                            {title}
                        </h2>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                            aria-label="Close"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="py-3.5 max-w-[560px] mx-auto">{stepIndicator}</div>
                </div>

                <form onSubmit={onSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <div className="flex-1 min-h-0 flex overflow-hidden">
                        <div className="flex-1 min-w-0 overflow-y-auto p-6 flex flex-col gap-5">
                            {children}
                        </div>
                        {summaryPane}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-3 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                        >
                            Cancel
                        </button>
                        <div className="ml-auto flex items-center gap-3">{primaryActions}</div>
                    </div>
                </form>
            </div>
        </div>
    );
}
